import React, { useRef, useEffect } from "react";
import CodeOfConductModal, { hasAcceptedCodeOfConduct } from "../modals/CodeOfConductModal";

// Multi-user Voice Chat Panel (WebRTC + socket signaling)
function GameRoomVoiceChat({ socket, socketId, voiceChatUsers, setVoiceChatUsers, setSpeakingUsers }) {
  const [joined, setJoined] = React.useState(false);
  const [muted, setMuted] = React.useState(false);
  const [peers, setPeers] = React.useState({}); // { peerId: RTCPeerConnection }
  const [remoteStreams, setRemoteStreams] = React.useState({}); // { peerId: MediaStream }
  const localStreamRef = React.useRef(null);
  const audioRefs = React.useRef({}); // { peerId: audioRef }
  const speakingIntervals = useRef({});
  const peersRef = React.useRef({}); // mirror of peers for stable lookups in callbacks
  const pendingCandidatesRef = React.useRef({}); // { peerId: [candidate, ...] }
  const audioCtxRef = React.useRef(null); // single AudioContext
  // developer debugLines removed (unused in production)

  // Provide an in-UI debug log so the developer can see signaling logs without
  // relying on DevTools filters. Monkeypatch console.log locally while
  // this component is mounted and copy messages into window.__voiceDebug.
  // React.useEffect(() => {
  //   try {
  //     window.__voiceDebug = window.__voiceDebug || [];
  //   } catch (e) {
  //     // ignore
  //   }
  //   const orig = console.log.bind(console);
  //   console.log = (...args) => {
  //     try {
  //       window.__voiceDebug = window.__voiceDebug || [];
  //       const line = args.map(a => {
  //         try { return typeof a === 'object' ? JSON.stringify(a) : String(a); } catch (e) { return String(a); }
  //       }).join(' ');
  //       window.__voiceDebug.push(line);
  //       if (window.__voiceDebug.length > 200) window.__voiceDebug.shift();
  //     } catch (e) {}
  //     try { orig(...args); } catch (e) {}
  //   };
  //   const iv = setInterval(() => {
  //     try { /* noop: keep window.__voiceDebug for manual inspection */ } catch (e) {}
  //   }, 400);
  //   return () => {
  //     clearInterval(iv);
  //     try { console.log = orig; } catch (e) {}
  //   };
  // }, []);

  // Join voice chat: get local audio, notify others
  const [showCoC, setShowCoC] = React.useState(false);
  const handleJoin = async () => {
    if(!hasAcceptedCodeOfConduct()){
      try { window.dispatchEvent(new CustomEvent('open-coc', { detail: { callback: () => handleJoin() } })); } catch(e){}
      return;
    }
    try {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      localStreamRef.current = stream;
      setJoined(true);
      setMuted(false);
      if (!socket) {
        alert("Could not join voice chat: socket connection not found. Please refresh or check your connection.");
        return;
      }
      // Delay emitting `voice-join` briefly so the effect which registers
      // signaling listeners has time to run and won't miss server events
      // like 'voice-existing' or incoming offers.
      setTimeout(() => {
        try { socket.emit("voice-join", { id: socketId }); } catch (e) {}
      }, 200);
      // Start local speaking detection
  startLocalSpeakingDetection(stream);
    } catch (err) {
      alert("Could not access microphone: " + err.message);
    }
  };

  // Leave voice chat: close all peer connections, stop local audio
  const handleLeave = () => {
    // close via ref for completeness
    Object.values(peersRef.current || {}).forEach((pc) => {
      try { pc.close(); } catch (e) {}
    });
    peersRef.current = {};
    setPeers({});
    setRemoteStreams({});
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    setJoined(false);
    if (socket) socket.emit("voice-leave", { id: socketId });
    stopLocalSpeakingDetection();
  };
  // Local speaking detection
  const startLocalSpeakingDetection = (stream) => {
    if (!stream) return;
    // ensure single AudioContext
    if (!audioCtxRef.current) audioCtxRef.current = new window.AudioContext();
    const audioCtx = audioCtxRef.current;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);
    const dataArray = new Uint8Array(analyser.fftSize);
    // smoothing and calibration
    let smooth = 0;
    let baseline = 0.0001;
    let calibSamples = 0;
    function detect() {
      analyser.getByteTimeDomainData(dataArray);
      // compute RMS normalized around 128
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      // calibrate baseline for first 10 samples (~1s)
      if (calibSamples < 10) {
        baseline = (baseline * calibSamples + rms) / (calibSamples + 1);
        calibSamples++;
      }
      // exponential smoothing
      smooth = 0.85 * smooth + 0.15 * rms;
      const threshold = Math.max(baseline * 3.0, 0.02);
      const speaking = smooth > threshold;
      socket.emit("voice-speaking", { id: socketId, speaking });
    }
    speakingIntervals.current.local = setInterval(detect, 100);
  };
  const stopLocalSpeakingDetection = () => {
    if (speakingIntervals.current.local) {
      clearInterval(speakingIntervals.current.local);
      speakingIntervals.current.local = null;
    }
  };

  // Remote speaking detection (basic, per peer)
  const startRemoteSpeakingDetection = React.useCallback((peerId, stream) => {
    if (!stream) return;
    if (!audioCtxRef.current) audioCtxRef.current = new window.AudioContext();
    const audioCtx = audioCtxRef.current;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);
    const dataArray = new Uint8Array(analyser.fftSize);
    let smooth = 0;
    let baseline = 0.0001;
    let calibSamples = 0;
    function detect() {
      analyser.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      if (calibSamples < 10) { baseline = (baseline * calibSamples + rms) / (calibSamples + 1); calibSamples++; }
      smooth = 0.85 * smooth + 0.15 * rms;
      const threshold = Math.max(baseline * 3.0, 0.02);
      if (smooth > threshold) {
        setSpeakingUsers(prev => Array.from(new Set([...prev, peerId])));
      } else {
        setSpeakingUsers(prev => prev.filter(id => id !== peerId));
      }
    }
    speakingIntervals.current[peerId] = setInterval(detect, 100);
  }, [setSpeakingUsers]);
  const stopRemoteSpeakingDetection = (peerId) => {
    if (speakingIntervals.current[peerId]) {
      clearInterval(speakingIntervals.current[peerId]);
      speakingIntervals.current[peerId] = null;
    }
  };

  // Mute/unmute local audio
  const handleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => track.enabled = false);
    }
    setMuted(true);
  };
  const handleUnmute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => track.enabled = true);
    }
    setMuted(false);
  };

  // Helper: create peer connection
  const createPeerConnection = React.useCallback((peerId) => {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        //console.log('VoiceChat: sending ICE candidate to', peerId);
        socket.emit("voice-ice-candidate", { to: peerId, candidate: event.candidate });
      }
    };
    pc.ontrack = (event) => {
      //console.log('VoiceChat: ontrack from', peerId);
      setRemoteStreams(prev => ({ ...prev, [peerId]: event.streams[0] }));
    };
    pc.onconnectionstatechange = () => {
      //console.log('VoiceChat: connectionState', peerId, pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed') {
        try { pc.close(); } catch (e) {}
        // cleanup
        peersRef.current = Object.fromEntries(Object.entries(peersRef.current || {}).filter(([k]) => k !== peerId));
        setPeers({ ...peersRef.current });
        setRemoteStreams(prev => { const copy = {...prev}; delete copy[peerId]; return copy; });
        setVoiceChatUsers(prev => prev.filter(id => id !== peerId));
        setSpeakingUsers(prev => prev.filter(id => id !== peerId));
      }
    };
    pc.oniceconnectionstatechange = () => {
      //console.log('VoiceChat: iceConnectionState', peerId, pc.iceConnectionState);
    };
    return pc;
  }, [socket, setVoiceChatUsers, setSpeakingUsers]);

  // Handle signaling events
  useEffect(() => {
    if (!socket || !joined) return;

  // Debug: log when joined and peers change
  console.log('VoiceChat: useEffect mounted - joined?', joined, 'socketId:', socketId, 'existing peers count:', Object.keys(peers).length);

    // When another user joins: record them in the voice list and *optionally*
    // create an offer based on a deterministic tie-breaker to avoid glare.
    socket.on("voice-peer-join", (peerId) => {
      console.log('VoiceChat: voice-peer-join', peerId);
      if (peerId === socketId) return;
  // parent `voiceChatUsers` is managed centrally in GameRoom; don't mutate here
      try {
        // Deterministic rule: only the client with a lexicographically greater
        // socket id initiates the offer. This ensures exactly one side offers.
        if (socketId && peerId && socketId > peerId) {
          if (peersRef.current[peerId]) return;
          console.debug('VoiceChat: peer-join creating pc+offer for', peerId);
          const pc = createPeerConnection(peerId);
          if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
          }
          peersRef.current = { ...peersRef.current, [peerId]: pc };
          setPeers({ ...peersRef.current });
          (async () => {
            try {
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              socket.emit("voice-offer", { to: peerId, offer });
            } catch (e) { console.debug('VoiceChat: error creating offer on peer-join', e); }
          })();
        }
      } catch (e) {}
    });

    // When joining, server may send a list of existing participants so we can
    // proactively create peer connections and send offers
    socket.on('voice-existing', async (existing) => {
  console.log('VoiceChat: voice-existing', existing);
  if (!Array.isArray(existing)) return;
  for (const peerId of existing) {
        if (peerId === socketId) continue;
        if (peersRef.current[peerId]) continue;
        console.log('VoiceChat: creating pc for existing peer (joiner offers)', peerId);
        const pc = createPeerConnection(peerId);
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
        }
        peersRef.current = { ...peersRef.current, [peerId]: pc };
        setPeers({ ...peersRef.current });
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          console.log('VoiceChat: sending offer to', peerId);
          socket.emit('voice-offer', { to: peerId, offer });
        } catch (e) {
          // ignore
        }
      }
    });

    // When receiving offer, create peer connection, set remote desc, send answer
    socket.on("voice-offer", async ({ from, offer }) => {
      console.log('VoiceChat: received voice-offer from', from);
      if (from === socketId) return;
      // Create or reuse peer connection for this offer
      let pc = peersRef.current[from];
      if (!pc) {
        pc = createPeerConnection(from);
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
        }
        peersRef.current = { ...peersRef.current, [from]: pc };
        setPeers({ ...peersRef.current });
      } else {
        // ensure local tracks present
        try {
          if (localStreamRef.current) {
            const senders = pc.getSenders().map(s => s.track).filter(Boolean);
            if (senders.length === 0) {
              localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
            }
          }
        } catch (e) {}
      }
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        // Drain any queued ICE candidates that arrived before remoteDescription was set
        try {
          const queued = pendingCandidatesRef.current[from] || [];
          for (const c of queued) {
            try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { /* ignore */ }
          }
          // after draining, clear the queue
          pendingCandidatesRef.current[from] = [];
        } catch (e) {}
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        console.debug('VoiceChat: sending answer to', from);
        socket.emit("voice-answer", { to: from, answer });
      } catch (e) {
        console.debug('VoiceChat: error handling offer', e);
      }
    });

    // When receiving answer, set remote desc
    socket.on("voice-answer", async ({ from, answer }) => {
    console.log('VoiceChat: received voice-answer from', from);
      const pc = peersRef.current[from];
      if (!pc) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        // Drain any queued ICE candidates that arrived before remoteDescription was set
        try {
          const queued = pendingCandidatesRef.current[from] || [];
          for (const c of queued) {
            try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { /* ignore individual candidate errors */ }
          }
      // Clear queued candidates after applying
      pendingCandidatesRef.current[from] = [];
        } catch (e) {}
        //console.log('VoiceChat: Received answer from', from);
      } catch (e) {
        //console.log('VoiceChat: error applying answer', e);
      }
    });

    // When receiving ICE candidate
    socket.on("voice-ice-candidate", ({ from, candidate }) => {
  console.log('VoiceChat: received ice-candidate from', from, 'hasPc:', !!peersRef.current[from]);
      if (!candidate) return;
      const pc = peersRef.current[from];
      // If we have a pc and its remoteDescription is set, add immediately.
      // Otherwise queue until remoteDescription is available to avoid InvalidStateError.
      if (pc && pc.remoteDescription && pc.remoteDescription.type) {
        try {
          pc.addIceCandidate(new RTCIceCandidate(candidate));
          //console.log('VoiceChat: Added ICE candidate from', from);
        } catch (e) {
          //console.log('VoiceChat: error adding ICE candidate', e);
        }
      } else {
        pendingCandidatesRef.current[from] = pendingCandidatesRef.current[from] || [];
        pendingCandidatesRef.current[from].push(candidate);
      }
    });

    // When a peer leaves
    socket.on("voice-peer-leave", (peerId) => {
  console.log('VoiceChat: voice-peer-leave', peerId);
      const pc = peersRef.current[peerId];
      if (pc) {
        try { pc.close(); } catch (e) {}
        //console.log('VoiceChat: Peer left', peerId);
      }
      peersRef.current = Object.fromEntries(Object.entries(peersRef.current || {}).filter(([k]) => k !== peerId));
      setPeers({ ...peersRef.current });
      setRemoteStreams(prev => { const copy = { ...prev }; delete copy[peerId]; return copy; });
  // cleanup any queued ICE candidates for this peer
  if (pendingCandidatesRef.current[peerId]) delete pendingCandidatesRef.current[peerId];
      setVoiceChatUsers(prev => prev.filter(id => id !== peerId));
      setSpeakingUsers(prev => prev.filter(id => id !== peerId));
    });

    return () => {
      socket.off("voice-peer-join");
      socket.off("voice-offer");
      socket.off("voice-answer");
      socket.off("voice-ice-candidate");
      socket.off("voice-peer-leave");
    };
  }, [socket, socketId, joined, createPeerConnection, localStreamRef, peers, setVoiceChatUsers, setSpeakingUsers ]);

  // Attach remote streams to audio elements and start speaking detection
  useEffect(() => {
    // Attach streams to audio elements in a single stable place to avoid
    // rapid attach/detach during render which causes controls to flicker.
    Object.entries(remoteStreams).forEach(([peerId, stream]) => {
      const audioEl = audioRefs.current[peerId];
      if (!audioEl) return;
      try {
        // Assign only when different to avoid resetting play state repeatedly
        if (audioEl.srcObject !== stream) {
          try { audioEl.pause(); audioEl.currentTime = 0; } catch (e) {}
          audioEl.srcObject = stream;
          audioEl.volume = 1;
          audioEl.load();
          audioEl.play().catch(() => {});
        }
      } catch (e) {}
      startRemoteSpeakingDetection(peerId, stream);
  // parent `voiceChatUsers` is managed centrally in GameRoom; don't mutate here
    });

    // Clean up detection & state for peers that no longer have streams
    Object.keys(speakingIntervals.current).forEach(peerId => {
      if (!remoteStreams[peerId] && peerId !== 'local') {
        stopRemoteSpeakingDetection(peerId);
        setSpeakingUsers(prev => prev.filter(id => id !== peerId));
        setVoiceChatUsers(prev => (prev || []).filter(id => id !== peerId));
      }
    });
  }, [remoteStreams, setSpeakingUsers, setVoiceChatUsers, startRemoteSpeakingDetection]);

  // Use voiceChatUsers from parent (actual array, not setter)
  const joinedCount = Array.isArray(voiceChatUsers) ? voiceChatUsers.length : 0;
  // Ensure we also listen for join/leave here in case parent misses events or
  // this component is mounted after events fired. Update parent state if setter provided.
  React.useEffect(() => {
    if (!socket || !setVoiceChatUsers) return;
    const handleJoin = (/* payload */) => {
      // GameRoom already listens for 'voice-join' and updates the list; skip here
    };
    const handleLeave = (payload) => {
      const id = payload?.id || payload;
      setVoiceChatUsers(prev => (prev || []).filter(x => x !== id));
    };
    socket.on('voice-join', handleJoin);
    socket.on('voice-leave', handleLeave);
    return () => {
      socket.off('voice-join', handleJoin);
      socket.off('voice-leave', handleLeave);
    };
  }, [socket, setVoiceChatUsers]);
  return (
    //<div className="mb-4 p-3 bg-blue-900 dark:bg-blue-900 rounded shadow flex flex-col items-start gap-2">
      <div className="flex flex-col items-start gap-2">
      {!joined ? (
        <>
        <button className="px-3 py-2 bg-blue-500 text-white rounded w-full text-center" onClick={handleJoin}>
          Join Voice Chat{` (${joinedCount} joined)`}
        </button>
        <CodeOfConductModal show={showCoC} onClose={()=>setShowCoC(false)} onAccept={()=>{ setShowCoC(false); handleJoin(); }} />
        </>
      ) : (
        <>
          <div className="flex gap-2 mb-2 w-full">
            <button className="px-3 py-2 bg-red-500 text-white rounded flex-1 text-left" onClick={handleLeave}>
              Leave Voice Chat{` (${joinedCount} joined)`}
            </button>
            {!muted ? (
              <button className="px-3 py-2  bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300 rounded text-center" onClick={handleMute}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M9 5a3 3 0 0 1 3-3h0a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h0a3 3 0 0 1-3-3z"/><path d="M5 10a7 7 0 0 0 14 0M8 21h8m-4-4v4"/></g></svg></button>
            ) : (
              <button className="px-3 py-2  bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300 rounded text-center" onClick={handleUnmute}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="m3 3l18 18M9 5a3 3 0 0 1 6 0v5a3 3 0 0 1-.13.874m-2 2A3 3 0 0 1 9 10.002v-1"/><path d="M5 10a7 7 0 0 0 10.846 5.85m2-2A6.97 6.97 0 0 0 18.998 10M8 21h8m-4-4v4"/></g></svg></button>
            )}
          </div>
          <audio autoPlay controls style={{ display: 'none' }} ref={el => {
            audioRefs.current['local'] = el;
            // Prevent local echo: do NOT attach localStream to local audio element
          }} />
          {/* Render remote audio for each peer, visible for user interaction */}
          {Object.keys(remoteStreams).map(peerId => (
            <div key={peerId} className="flex items-center gap-2 my-1">
              {/* <span className="text-xs text-gray-600 dark:text-gray-300">Peer: {peerId}</span> */}
              <audio
                autoPlay
                //controls
                style={{ minWidth: 120, maxWidth: 120 }}
                ref={el => {
                  audioRefs.current[peerId] = el;
                  // If the remote stream arrived before the audio element was
                  // mounted, attach it now. Avoid pausing/resetting to prevent
                  // flicker when re-attaching; only attach if different.
                  try {
                    const stream = remoteStreams[peerId];
                    if (el && stream && el.srcObject !== stream) {
                      el.srcObject = stream;
                      el.volume = 1;
                      el.load();
                      el.play().catch(() => {});
                    }
                  } catch (e) {}
                }}
                data-peerid={peerId}
              />
            </div>
          ))}
          {/* <div className="text-xs text-gray-700 dark:text-gray-200 mt-2">
            Connected peers: {Object.keys(peers).length}
          </div> */}
          {/* <div className="mt-2 p-2 text-xs bg-white dark:bg-gray-800 border rounded max-h-40 overflow-auto w-full">
            <div className="font-semibold">Voice Debug (latest)</div>
            {debugLines.slice(-8).reverse().map((l, idx) => (
              <div key={idx} className="text-[10px] text-gray-700 dark:text-gray-300">{l}</div>
            ))}
          </div> */}
        </>
      )}
    </div>
  );
}

export default GameRoomVoiceChat;
